/**
 * BrightPath - useExpenseFilters Hook
 * BP-04-04: Expenses Frontend
 */

import { useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ExpenseFilters, ExpenseCategory } from '../types';

interface UseExpenseFiltersOptions {
  syncWithUrl?: boolean;
  defaultFilters?: Partial<ExpenseFilters>;
}

interface UseExpenseFiltersReturn {
  filters: ExpenseFilters;
  setFilter: <K extends keyof ExpenseFilters>(key: K, value: ExpenseFilters[K]) => void;
  setFilters: (filters: Partial<ExpenseFilters>) => void;
  clearFilters: () => void;
  clearFilter: (key: keyof ExpenseFilters) => void;
  hasActiveFilters: boolean;
  activeFilterCount: number;
}

const DEFAULT_FILTERS: ExpenseFilters = {
  category: null,
  childId: null,
  startDate: null,
  endDate: null,
  minAmount: null,
  maxAmount: null,
  isTaxDeductible: null,
  isInsuranceCovered: null,
  vendorName: null,
  tags: [],
  search: null,
};

/**
 * Hook for managing expense filters with optional URL sync
 */
export function useExpenseFilters(
  options: UseExpenseFiltersOptions = {}
): UseExpenseFiltersReturn {
  const { syncWithUrl = false, defaultFilters = {} } = options;
  const [searchParams, setSearchParams] = useSearchParams();

  // Parse filters from URL if syncing
  const getInitialFilters = useCallback((): ExpenseFilters => {
    if (!syncWithUrl) {
      return { ...DEFAULT_FILTERS, ...defaultFilters };
    }

    return {
      category: (searchParams.get('category') as ExpenseCategory) || defaultFilters.category || null,
      childId: searchParams.get('childId') || defaultFilters.childId || null,
      startDate: searchParams.get('startDate') || defaultFilters.startDate || null,
      endDate: searchParams.get('endDate') || defaultFilters.endDate || null,
      minAmount: searchParams.get('minAmount')
        ? Number(searchParams.get('minAmount'))
        : defaultFilters.minAmount || null,
      maxAmount: searchParams.get('maxAmount')
        ? Number(searchParams.get('maxAmount'))
        : defaultFilters.maxAmount || null,
      isTaxDeductible: searchParams.has('isTaxDeductible')
        ? searchParams.get('isTaxDeductible') === 'true'
        : defaultFilters.isTaxDeductible ?? null,
      isInsuranceCovered: searchParams.has('isInsuranceCovered')
        ? searchParams.get('isInsuranceCovered') === 'true'
        : defaultFilters.isInsuranceCovered ?? null,
      vendorName: searchParams.get('vendorName') || defaultFilters.vendorName || null,
      tags: searchParams.get('tags')?.split(',').filter(Boolean) || defaultFilters.tags || [],
      search: searchParams.get('search') || defaultFilters.search || null,
    };
  }, [searchParams, syncWithUrl, defaultFilters]);

  const [filters, setFiltersState] = useState<ExpenseFilters>(getInitialFilters);

  // Sync filters to URL
  const syncToUrl = useCallback(
    (newFilters: ExpenseFilters) => {
      if (!syncWithUrl) return;

      const params = new URLSearchParams();

      Object.entries(newFilters).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '') return;
        if (Array.isArray(value) && value.length === 0) return;

        if (Array.isArray(value)) {
          params.set(key, value.join(','));
        } else if (typeof value === 'boolean') {
          params.set(key, value.toString());
        } else if (typeof value === 'number') {
          params.set(key, value.toString());
        } else {
          params.set(key, value);
        }
      });

      setSearchParams(params, { replace: true });
    },
    [syncWithUrl, setSearchParams]
  );

  // Set a single filter
  const setFilter = useCallback(
    <K extends keyof ExpenseFilters>(key: K, value: ExpenseFilters[K]) => {
      setFiltersState((prev) => {
        const newFilters = { ...prev, [key]: value };
        syncToUrl(newFilters);
        return newFilters;
      });
    },
    [syncToUrl]
  );

  // Set multiple filters at once
  const setFilters = useCallback(
    (newFilters: Partial<ExpenseFilters>) => {
      setFiltersState((prev) => {
        const merged = { ...prev, ...newFilters };
        syncToUrl(merged);
        return merged;
      });
    },
    [syncToUrl]
  );

  // Clear all filters
  const clearFilters = useCallback(() => {
    const cleared = { ...DEFAULT_FILTERS, ...defaultFilters };
    setFiltersState(cleared);
    syncToUrl(cleared);
  }, [defaultFilters, syncToUrl]);

  // Clear a single filter
  const clearFilter = useCallback(
    (key: keyof ExpenseFilters) => {
      setFiltersState((prev) => {
        const newFilters = {
          ...prev,
          [key]: DEFAULT_FILTERS[key],
        };
        syncToUrl(newFilters);
        return newFilters;
      });
    },
    [syncToUrl]
  );

  // Count active filters
  const { hasActiveFilters, activeFilterCount } = useMemo(() => {
    let count = 0;

    if (filters.category) count++;
    if (filters.childId) count++;
    if (filters.startDate || filters.endDate) count++;
    if (filters.minAmount || filters.maxAmount) count++;
    if (filters.isTaxDeductible !== null) count++;
    if (filters.isInsuranceCovered !== null) count++;
    if (filters.vendorName) count++;
    if (filters.tags && filters.tags.length > 0) count++;
    if (filters.search) count++;

    return {
      hasActiveFilters: count > 0,
      activeFilterCount: count,
    };
  }, [filters]);

  return {
    filters,
    setFilter,
    setFilters,
    clearFilters,
    clearFilter,
    hasActiveFilters,
    activeFilterCount,
  };
}

/**
 * Hook for date range presets
 */
export function useDateRangePresets() {
  const presets = useMemo(() => {
    const now = new Date();
    const today = now.toISOString().split('T')[0];

    // This month
    const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const thisMonthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    // Last month
    const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);

    // This year
    const thisYearStart = new Date(now.getFullYear(), 0, 1);

    // Last year
    const lastYearStart = new Date(now.getFullYear() - 1, 0, 1);
    const lastYearEnd = new Date(now.getFullYear() - 1, 11, 31);

    // Last 30 days
    const last30Days = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

    // Last 90 days
    const last90Days = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);

    return [
      {
        label: 'This Month',
        startDate: thisMonthStart.toISOString().split('T')[0],
        endDate: thisMonthEnd.toISOString().split('T')[0],
      },
      {
        label: 'Last Month',
        startDate: lastMonthStart.toISOString().split('T')[0],
        endDate: lastMonthEnd.toISOString().split('T')[0],
      },
      {
        label: 'Last 30 Days',
        startDate: last30Days.toISOString().split('T')[0],
        endDate: today,
      },
      {
        label: 'Last 90 Days',
        startDate: last90Days.toISOString().split('T')[0],
        endDate: today,
      },
      {
        label: 'This Year',
        startDate: thisYearStart.toISOString().split('T')[0],
        endDate: today,
      },
      {
        label: 'Last Year',
        startDate: lastYearStart.toISOString().split('T')[0],
        endDate: lastYearEnd.toISOString().split('T')[0],
      },
    ];
  }, []);

  return presets;
}
